import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import {
  Actions,
  Async,
  Dispatchers,
  toAsyncFetched,
} from '@client/new/helpers/types';
import { getDispatchers } from '@client/new/helpers/types-utils';
import { useDebounce } from '@client/hooks/use-debounce';
import { usePortalUserListModel } from '../model/PortalUserList.model';
import {
  PortalUser,
  PortalUserInput,
  PortalUserRole,
} from '../model/PortalUserList.types';
import {
  filterPortalUsers,
  PortalUserLinkedFilter,
  PortalUserSortField,
  PortalUserStatusFilter,
  sortPortalUsers,
} from './PortalUserList.filterEngine';
import { PortalUserRowVM, toRowVM } from './PortalUserList.viewModel.mappers';

const STORAGE_KEY = 'portalUserListFilters';

type StoredFilters = {
  search: string;
  role: PortalUserRole | null;
  status: PortalUserStatusFilter;
  linked: PortalUserLinkedFilter;
  sort: PortalUserSortField;
};

const DEFAULT_FILTERS: StoredFilters = {
  search: '',
  role: null,
  status: 'active',
  linked: 'all',
  sort: 'id',
};

const loadStoredFilters = (): StoredFilters => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_FILTERS;
    const parsed = JSON.parse(raw) as Partial<StoredFilters>;
    return { ...DEFAULT_FILTERS, ...parsed };
  } catch {
    return DEFAULT_FILTERS;
  }
};

const saveFilters = (filters: StoredFilters): void => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filters));
  } catch {
    // localStorage unavailable; persistence is best-effort.
  }
};

export type PortalUserListActionMap = {
  SetSearch: string;
  SetRole: PortalUserRole | null;
  SetStatus: PortalUserStatusFilter;
  SetLinked: PortalUserLinkedFilter;
  SetSort: PortalUserSortField;
  TogglePortalUser: number;
  DeletePortalUser: number;
  ClearAllFilters: null;
  OpenCreate: null;
  OpenEdit: number;
  CloseEditor: null;
};

export type PortalUserFormTarget =
  | { type: 'closed' }
  | { type: 'create' }
  | { type: 'edit'; user: PortalUser };

export type PortalUserListTableVM = {
  filteredSorted: PortalUser[];
  totalCount: number;
  visibleCount: number;
};

export type PortalUserListVM = {
  tableState: Async<PortalUserListTableVM>;
  search: string;
  role: PortalUserRole | null;
  status: PortalUserStatusFilter;
  linked: PortalUserLinkedFilter;
  sort: PortalUserSortField;
  pendingIds: number[];
  deletingIds: number[];
  toRowVM: (user: PortalUser) => PortalUserRowVM;
  editor: PortalUserFormTarget;
  savePortalUser: (input: PortalUserInput) => Promise<void>;
} & Dispatchers<PortalUserListActionMap>;

const DELETE_CONFIRM_COPY = 'Are you sure you want to delete this portal user?';

// A ?q= link is how people look a user up by e-mail, and the legacy list
// showed disabled matches too, so the URL search also lifts the status filter.
const loadFilters = (urlSearch: string | null): StoredFilters => {
  const stored = loadStoredFilters();
  return urlSearch === null
    ? stored
    : { ...stored, search: urlSearch, status: 'all' };
};

export const usePortalUserListViewModel = ({
  urlSearch,
}: {
  urlSearch: string | null;
}): Async<PortalUserListVM> => {
  const [stored] = useState(() => loadFilters(urlSearch));

  const [search, setSearch] = useState(stored.search);
  const [role, setRole] = useState<PortalUserRole | null>(stored.role);
  const [status, setStatus] = useState<PortalUserStatusFilter>(stored.status);
  const [linked, setLinked] = useState<PortalUserLinkedFilter>(stored.linked);
  const [sort, setSort] = useState<PortalUserSortField>(stored.sort);
  const [pendingIds, setPendingIds] = useState<number[]>([]);
  const [deletingIds, setDeletingIds] = useState<number[]>([]);
  const [editor, setEditor] = useState<PortalUserFormTarget>({
    type: 'closed',
  });

  const debouncedSearch = useDebounce(search, 200);

  const asyncModel = usePortalUserListModel();
  const asyncModelRef = useRef(asyncModel);
  asyncModelRef.current = asyncModel;

  useEffect(() => {
    saveFilters({ search, role, status, linked, sort });
  }, [search, role, status, linked, sort]);

  const togglePortalUser = useCallback(async (id: number): Promise<void> => {
    if (asyncModelRef.current.type !== 'fetched') return;

    setPendingIds((prev) => [...prev, id]);

    try {
      await asyncModelRef.current.value.togglePortalUser(id);
      toast.success('User updated');
    } catch {
      toast.error("Couldn't update user");
      if (asyncModelRef.current.type === 'fetched') {
        await asyncModelRef.current.value.refetch();
      }
    } finally {
      setPendingIds((prev) => prev.filter((currentId) => currentId !== id));
    }
  }, []);

  const deletePortalUser = useCallback(async (id: number): Promise<void> => {
    if (asyncModelRef.current.type !== 'fetched') return;

    const confirmed = window.confirm(DELETE_CONFIRM_COPY);
    if (!confirmed) return;

    setDeletingIds((prev) => [...prev, id]);

    try {
      await asyncModelRef.current.value.deletePortalUser(id);
      toast.success('User deleted');
    } catch {
      toast.error("Couldn't delete user");
    } finally {
      setDeletingIds((prev) => prev.filter((currentId) => currentId !== id));
    }
  }, []);

  const _dispatch = (action: Actions<PortalUserListActionMap>) => {
    switch (action.type) {
      case 'SetSearch':
        setSearch(action.payload);
        return;
      case 'SetRole':
        setRole(action.payload);
        return;
      case 'SetStatus':
        setStatus(action.payload);
        return;
      case 'SetLinked':
        setLinked(action.payload);
        return;
      case 'SetSort':
        setSort(action.payload);
        return;
      case 'TogglePortalUser':
        void togglePortalUser(action.payload);
        return;
      case 'DeletePortalUser':
        void deletePortalUser(action.payload);
        return;
      case 'ClearAllFilters':
        setSearch(DEFAULT_FILTERS.search);
        setRole(DEFAULT_FILTERS.role);
        setStatus(DEFAULT_FILTERS.status);
        setLinked(DEFAULT_FILTERS.linked);
        setSort(DEFAULT_FILTERS.sort);
        return;
      case 'OpenCreate':
        setEditor({ type: 'create' });
        return;
      case 'OpenEdit': {
        if (asyncModel.type !== 'fetched') return;
        const user = asyncModel.value.portalUsers.find(
          ({ id }) => id === action.payload,
        );
        if (user) setEditor({ type: 'edit', user });
        return;
      }
      case 'CloseEditor':
        setEditor({ type: 'closed' });
        return;
    }
  };

  const dispatchers = getDispatchers<PortalUserListActionMap>(_dispatch);

  const tableState = useMemo((): Async<PortalUserListTableVM> => {
    if (asyncModel.type !== 'fetched') return asyncModel;

    const filtered = filterPortalUsers(asyncModel.value.portalUsers, {
      search: debouncedSearch,
      role,
      status,
      linked,
    });
    const filteredSorted = sortPortalUsers(filtered, sort);

    return toAsyncFetched({
      filteredSorted,
      totalCount: asyncModel.value.portalUsers.length,
      visibleCount: filteredSorted.length,
    });
  }, [asyncModel, debouncedSearch, role, status, linked, sort]);

  const buildRowVM = useCallback(
    (user: PortalUser): PortalUserRowVM =>
      toRowVM(user, {
        isPending: pendingIds.includes(user.id),
        isDeleting: deletingIds.includes(user.id),
      }),
    [pendingIds, deletingIds],
  );

  const savePortalUser = useCallback(async (input: PortalUserInput) => {
    if (asyncModelRef.current.type !== 'fetched') {
      throw new Error('Users are not loaded yet');
    }
    await asyncModelRef.current.value.savePortalUser(input);
  }, []);

  return toAsyncFetched<PortalUserListVM>({
    tableState,
    search,
    role,
    status,
    linked,
    sort,
    pendingIds,
    deletingIds,
    toRowVM: buildRowVM,
    editor,
    savePortalUser,
    ...dispatchers,
  });
};
